import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  PageHeader,
  Input,
  Avatar,
  Result,
  Tabs,
  Button,
  Tooltip,
} from 'antd';
import {
  FileTextOutlined,
  UserOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { MyBreadcrumb, handleApiError } from 'utils';
import { usersListService, usersSearchService } from 'services/user';
import Auth from 'auth';
import { debounce } from 'lodash';
import { legalAcceptanceColumns } from './legalAcceptanceColumns';

const { Search } = Input;
const { TabPane } = Tabs;

function formatUserRole(role) {
  switch (role) {
    case 10:
      return '10 - Super Admin';
    case 11:
      return '11 - Super Admin Assistant';
    case 12:
      return '12 - Workspace Managers';
    case 13:
      return '13 - Super Admin Assistant Tech Six';
    case 30:
      return '30 - Reseller Non-Admin and Reseller Admin';
    case 40:
      return '40 - Customer Company Admin';
    case 50:
      return '50 - Customer Branch Admin';
    case 60:
      return '60 - Customer Non Admin';
    default:
      return `${role} - Custom Role`;
  }
}

export default function LegalAcceptanceList() {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const isSuperAdmin = Auth.getRole() === 10;

  const loadData = (q) => {
    setIsLoading(true);
    const request = q ? usersSearchService(q) : usersListService();
    request
      .then((response) => {
        setIsLoading(false);
        const nextItems = (response.data.items || []).map((item) => ({
          ...item,
          roleLabel: formatUserRole(item.role),
        }));
        setItems(nextItems);
      })
      .catch((error) => {
        setIsLoading(false);
        handleApiError(error);
      });
  };

  useEffect(() => {
    if (isSuperAdmin) {
      loadData();
    }
  }, [isSuperAdmin]);

  const delayedQuery = debounce((q) => loadData(q), 500);

  if (!isSuperAdmin) {
    return (
      <Result
        status="403"
        title="Access denied"
        subTitle="Only Super Admin can review Terms of Service acceptance."
      />
    );
  }

  const pending = items.filter((item) => !item.legal_accepted_at);
  const accepted = items.filter((item) => item.legal_accepted_at);
  const tableData =
    activeTab === 'pending'
      ? pending
      : activeTab === 'accepted'
        ? accepted
        : items;

  const columns = [
    {
      title: 'Photo',
      key: 'photo',
      render: (text, record) =>
        record.photo ? (
          <Avatar src={record.photo} />
        ) : (
          <Avatar icon={<UserOutlined />} />
        ),
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
      sorter: (a, b) =>
        String(a.display_name || '').localeCompare(String(b.display_name || '')),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => String(a.email || '').localeCompare(String(b.email || '')),
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
      render: (text, record) => record.company?.company_name || '—',
    },
    {
      title: 'Role',
      dataIndex: 'roleLabel',
      key: 'role',
    },
    ...legalAcceptanceColumns,
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <Tooltip title="View">
          <Button
            onClick={() => history.push('/users/view/' + record._id)}
            type="primary"
            shape="circle"
            icon={<EyeOutlined />}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'Users', path: '/users' },
          { label: 'Terms Acceptance', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        avatar={{ icon: <FileTextOutlined /> }}
        title="Terms of Service Acceptance"
        subTitle="Who has accepted and who is still pending"
        extra={[
          <Search
            key="search"
            allowClear
            onChange={(e) => delayedQuery(e.target.value)}
            loading={isLoading}
            placeholder="Filter users"
            onSearch={(value) => loadData(value)}
            style={{ width: 220 }}
          />,
        ]}
      />
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={`Pending (${pending.length})`} key="pending" />
        <TabPane tab={`Accepted (${accepted.length})`} key="accepted" />
        <TabPane tab={`All (${items.length})`} key="all" />
      </Tabs>
      <Table
        dataSource={tableData}
        columns={columns}
        loading={isLoading}
        rowKey="_id"
      />
    </div>
  );
}
